import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, FileText, Lock, Unlock, Check, X, HelpCircle, Keyboard } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient } from '../utils/apiClient';

interface Document {
  id: string;
  filename: string;
  documentType: string;
  status: string;
  initialExtractionData: any;
  goldenData: any;
  filePath?: string;
}

interface FieldValidation {
  [key: string]: boolean; // true = validated and locked, false = needs validation
}

export const DocumentValidation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [document, setDocument] = useState<Document | null>(null);
  const [editedData, setEditedData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validatedFields, setValidatedFields] = useState<FieldValidation>({});
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [allFields, setAllFields] = useState<string[]>([]);

  useEffect(() => {
    if (id) {
      fetchDocument(id);
    }
  }, [id]);

  useEffect(() => {
    if (editedData) {
      const fields = extractAllFields(editedData);
      setAllFields(fields);
    }
  }, [editedData]);

  // Auto-scroll to current field
  useEffect(() => {
    if (allFields[currentFieldIndex]) {
      const element = window.document.getElementById(`field-${allFields[currentFieldIndex]}`);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
      }
    }
  }, [currentFieldIndex, allFields]);

  // Auto-save when all fields are validated
  useEffect(() => {
    const validatedCount = Object.values(validatedFields).filter(Boolean).length;
    const totalFields = allFields.length;
    
    if (totalFields > 0 && validatedCount === totalFields && !saving) {
      // Small delay to allow user to see the completion
      const timer = setTimeout(() => {
        handleSave();
        toast.success('Všechna pole ověřena! Automaticky ukládám jako Golden Data...');
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [validatedFields, allFields, saving]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle shortcuts when not typing in an input
      const activeElement = window.document.activeElement;
      if (activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA') {
        return;
      }

      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          if (allFields[currentFieldIndex]) {
            toggleFieldValidation(allFields[currentFieldIndex]);
          }
          break;
        case 'ArrowDown':
        case 'j':
          e.preventDefault();
          setCurrentFieldIndex(prev => Math.min(prev + 1, allFields.length - 1));
          break;
        case 'ArrowUp':
        case 'k':
          e.preventDefault();
          setCurrentFieldIndex(prev => Math.max(prev - 1, 0));
          break;
        case 's':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleSave();
          }
          break;
        case '?':
          e.preventDefault();
          setShowHelp(true);
          break;
        case 'Escape':
          setShowHelp(false);
          break;
      }
    };

    window.document.addEventListener('keydown', handleKeyDown);
    return () => window.document.removeEventListener('keydown', handleKeyDown);
  }, [currentFieldIndex, allFields]);

  const extractAllFields = (data: any, path: string = ''): string[] => {
    const fields: string[] = [];
    
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      Object.entries(data).forEach(([key, value]) => {
        if (key === 'meta') return; // Skip meta data
        
        const fullPath = path ? `${path}.${key}` : key;
        
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          fields.push(...extractAllFields(value, fullPath));
        } else if (Array.isArray(value)) {
          value.forEach((item, index) => {
            if (typeof item === 'object') {
              fields.push(...extractAllFields(item, `${fullPath}[${index}]`));
            } else {
              // Only include array items that have actual values
              if (item !== null && item !== undefined && item !== '') {
                fields.push(`${fullPath}[${index}]`);
              }
            }
          });
        } else {
          // Only include fields that have actual values
          if (value !== null && value !== undefined && value !== '') {
            fields.push(fullPath);
          }
        }
      });
    }
    
    return fields;
  };

  const fetchDocument = async (documentId: string) => {
    try {
      const response = await apiClient.get(`/api/documents/${documentId}`);
      const doc = response.data.document;
      setDocument(doc);
      setEditedData(doc.goldenData || doc.initialExtractionData || {});
      
      // Load document file for preview
      try {
        const fileResponse = await apiClient.get(`/api/documents/${documentId}/file`, {
          responseType: 'blob'
        });
        const fileUrl = URL.createObjectURL(fileResponse.data);
        setDocumentUrl(fileUrl);
      } catch (fileError) {
        console.warn('Failed to load document file:', fileError);
      }
    } catch (error) {
      console.error('Failed to fetch document:', error);
      toast.error('Failed to load document');
      navigate('/documents');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!document) return;

    setSaving(true);
    try {
      await apiClient.put(`/api/documents/${document.id}/golden`, {
        goldenData: editedData
      });
      
      toast.success('Golden data saved successfully');
      navigate('/documents');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save golden data');
    } finally {
      setSaving(false);
    }
  };

  // Helper function to deep clone an object
  const deepClone = (obj: any): any => {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime());
    if (Array.isArray(obj)) return obj.map(item => deepClone(item));
    
    const cloned: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        cloned[key] = deepClone(obj[key]);
      }
    }
    return cloned;
  };

  // Helper function to get nested value
  const getNestedValue = (obj: any, path: string): any => {
    if (!obj || !path) return undefined;
    const keys = path.split(/[\.\[\]]+/).filter(key => key !== '');
    let current = obj;
    
    for (const key of keys) {
      if (current === null || current === undefined) {
        return undefined;
      }
      current = current[key];
    }
    
    return current;
  };

  // Helper function to set nested value
  const setNestedValue = (obj: any, path: string, value: any): void => {
    const keys = path.split(/[\.\[\]]+/).filter(key => key !== '');
    let current = obj;
    
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      const nextKey = keys[i + 1];
      
      if (!(key in current)) {
        // Create array if next key is a number, otherwise create object
        current[key] = /^\d+$/.test(nextKey) ? [] : {};
      }
      current = current[key];
    }
    
    current[keys[keys.length - 1]] = value;
  };

  const updateNestedValue = (path: string, value: any) => {
    const newData = deepClone(editedData);
    setNestedValue(newData, path, value);
    setEditedData(newData);
  };

  const toggleFieldValidation = (path: string) => {
    setValidatedFields(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  const getFieldLabel = (key: string) => {
    const translations: Record<string, string> = {
      'amount': 'Částka',
      'amount_total': 'Celková částka',
      'amount_without_vat': 'Částka bez DPH',
      'vat_amount': 'DPH',
      'currency': 'Měna',
      'date_due': 'Datum splatnosti',
      'date_issue': 'Datum vystavení',
      'date_received': 'Datum přijetí',
      'supplier': 'Dodavatel',
      'customer': 'Zákazník',
      'invoice_number': 'Číslo faktury',
      'order_number': 'Číslo objednávky',
      'variable_symbol': 'Variabilní symbol',
      'constant_symbol': 'Konstantní symbol',
      'specific_symbol': 'Specifický symbol',
      'bank_account': 'Bankovní účet',
      'iban': 'IBAN',
      'swift': 'SWIFT',
      'name': 'Název',
      'address': 'Adresa',
      'city': 'Město',
      'zip': 'PSČ',
      'country': 'Země',
      'vat_number': 'DIČ',
      'registration_number': 'IČO',
      'email': 'Email',
      'phone': 'Telefon',
      'items': 'Položky',
      'description': 'Popis',
      'quantity': 'Množství',
      'unit_price': 'Jednotková cena',
      'vat_rate': 'Sazba DPH'
    };
    
    return translations[key] || key.replace(/_/g, ' ');
  };

  const isDateField = (key: string, value: any) => {
    const dateFields = ['date_due', 'date_issue', 'date_received', 'created_at', 'updated_at'];
    return dateFields.includes(key) || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value));
  };

  const formatDateValue = (value: string) => {
    try {
      const date = new Date(value);
      return date.toLocaleDateString('cs-CZ');
    } catch {
      return value;
    }
  };

  const renderField = (key: string, value: any, path: string = '') => {
    const fullPath = path ? `${path}.${key}` : key;
    const isValidated = validatedFields[fullPath];
    const fieldLabel = getFieldLabel(key);
    const isDate = isDateField(key, value);
    const isCurrentField = allFields[currentFieldIndex] === fullPath;

    // Skip originally empty fields, but keep fields that were filled and then emptied
    const originalValue = getNestedValue(document?.initialExtractionData, fullPath);
    const wasOriginallyEmpty = originalValue === null || originalValue === undefined || originalValue === '';
    const isCurrentlyEmpty = value === null || value === undefined || value === '';
    
    // Only hide if it was originally empty AND is currently empty
    if (wasOriginallyEmpty && isCurrentlyEmpty) {
      return null;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      // Filter out originally empty sub-fields
      const nonEmptyEntries = Object.entries(value).filter(([subKey, subValue]) => {
        const originalSubValue = getNestedValue(document?.initialExtractionData, `${fullPath}.${subKey}`);
        const wasOriginallyEmpty = originalSubValue === null || originalSubValue === undefined || originalSubValue === '';
        const isCurrentlyEmpty = subValue === null || subValue === undefined || subValue === '';
        
        // Keep if it wasn't originally empty OR if it's currently not empty
        return !wasOriginallyEmpty || !isCurrentlyEmpty;
      });
      
      // If no non-empty sub-fields, don't render the object at all
      if (nonEmptyEntries.length === 0) {
        return null;
      }
      
      return (
        <div key={fullPath} className="mb-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            {fieldLabel}
          </h4>
          <div className="pl-4 border-l-2 border-gray-200">
            {nonEmptyEntries.map(([subKey, subValue]) =>
              renderField(subKey, subValue, fullPath)
            )}
          </div>
        </div>
      );
    }

    if (Array.isArray(value)) {
      // Filter out empty array items
      const nonEmptyItems = value.filter(item => 
        item !== null && item !== undefined && item !== ''
      );
      
      // If no non-empty items, don't render the array at all
      if (nonEmptyItems.length === 0) {
        return null;
      }
      
      return (
        <div key={fullPath} className="mb-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            {fieldLabel}
          </h4>
          <div className="space-y-4">
            {nonEmptyItems.map((item, index) => (
              <div key={index} className="p-4 border border-gray-200 rounded-lg">
                <h5 className="text-sm font-medium text-gray-700 mb-2">
                  Položka {index + 1}
                </h5>
                {typeof item === 'object' ? (
                  Object.entries(item)
                    .filter(([, itemValue]) => itemValue !== null && itemValue !== undefined && itemValue !== '')
                    .map(([itemKey, itemValue]) =>
                      renderField(itemKey, itemValue, `${fullPath}[${index}]`)
                    )
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Hodnota</span>
                      <button
                        onClick={() => toggleFieldValidation(`${fullPath}[${index}]`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleFieldValidation(`${fullPath}[${index}]`);
                          }
                        }}
                        className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          validatedFields[`${fullPath}[${index}]`]
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {validatedFields[`${fullPath}[${index}]`] ? (
                          <>
                            <Lock className="h-3 w-3" />
                            <span>Ověřeno</span>
                          </>
                        ) : (
                          <>
                            <Unlock className="h-3 w-3" />
                            <span>Ověřit</span>
                          </>
                        )}
                      </button>
                    </div>
                    <input
                      type="text"
                      className={`input transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${validatedFields[`${fullPath}[${index}]`] ? 'bg-green-50 border-green-300' : ''}`}
                      value={String(item)}
                      onChange={(e) => {
                        const newArray = [...value];
                        newArray[index] = e.target.value;
                        updateNestedValue(fullPath, newArray);
                      }}
                      disabled={validatedFields[`${fullPath}[${index}]`]}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div 
        key={fullPath} 
        id={`field-${fullPath}`}
        className={`mb-4 p-2 rounded transition-colors ${isCurrentField ? 'bg-blue-50 border-2 border-blue-300' : ''}`}
      >
        <div className="flex items-center justify-between mb-2">
          <label className="label">{fieldLabel}</label>
          <button
            onClick={() => toggleFieldValidation(fullPath)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleFieldValidation(fullPath);
              }
            }}
            className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              isValidated
                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {isValidated ? (
              <>
                <Lock className="h-3 w-3" />
                <span>Ověřeno</span>
              </>
            ) : (
              <>
                <Unlock className="h-3 w-3" />
                <span>Ověřit</span>
              </>
            )}
          </button>
        </div>
        {isDate ? (
          <div className="flex space-x-2">
            <input
              type="date"
              className={`input flex-1 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${isValidated ? 'bg-green-50 border-green-300' : ''}`}
              value={value ? new Date(value).toISOString().split('T')[0] : ''}
              onChange={(e) => updateNestedValue(fullPath, e.target.value)}
              disabled={isValidated}
            />
            <div className="text-sm text-gray-500 self-center">
              {value ? formatDateValue(value) : ''}
            </div>
          </div>
        ) : (
          <input
            type="text"
            className={`input transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${isValidated ? 'bg-green-50 border-green-300' : ''}`}
            value={String(value)}
            onChange={(e) => updateNestedValue(fullPath, e.target.value)}
            disabled={isValidated}
          />
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          <div className="grid grid-cols-2 gap-6">
            <div className="h-96 bg-gray-200 rounded"></div>
            <div className="space-y-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="max-w-7xl mx-auto text-center py-12">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Document not found</p>
      </div>
    );
  }

  const validatedCount = Object.values(validatedFields).filter(Boolean).length;
  const totalFields = allFields.length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/documents')}
              className="btn-secondary"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zpět
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Validace dokumentu</h1>
              <p className="text-gray-600">{document.filename}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-600">
              Ověřeno: {validatedCount} / {totalFields} polí
            </div>
            <div className="w-32 h-2 bg-gray-200 rounded-full">
              <div
                className="h-2 bg-green-500 rounded-full transition-all duration-300"
                style={{ width: `${totalFields > 0 ? (validatedCount / totalFields) * 100 : 0}%` }}
              />
            </div>
            <button
              onClick={() => setShowHelp(true)}
              className="btn-secondary focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Zobrazit nápovědu (stiskněte ?)"
            >
              <HelpCircle className="h-4 w-4 mr-2" />
              Nápověda
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-success focus:outline-none focus:ring-2 focus:ring-green-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSave();
                }
              }}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Ukládání...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Uložit jako Golden Data
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-80px)]">
        {/* Document Preview */}
        <div className="w-1/2 p-6">
          <div className="bg-white rounded-lg shadow-sm h-full overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Náhled dokumentu</h3>
            </div>
            <div className="h-full p-4 overflow-auto">
              {documentUrl ? (
                <div className="h-full">
                  {document.filename?.toLowerCase().endsWith('.pdf') ? (
                    <iframe
                      src={documentUrl}
                      className="w-full h-full border border-gray-300 rounded"
                      title="Document Preview"
                    />
                  ) : (
                    <div className="h-full overflow-auto">
                      <img
                        src={documentUrl}
                        alt="Document Preview"
                        className="w-full h-auto min-h-full object-contain cursor-zoom-in hover:cursor-zoom-out"
                        style={{ minWidth: '100%' }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Načítání náhledu...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Validation Form */}
        <div className="w-1/2 p-6">
          <div className="bg-white rounded-lg shadow-sm h-full overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Vytěžená data</h3>
                <span className={`badge ${
                  document.status === 'golden' ? 'badge-success' : 'badge-warning'
                }`}>
                  {document.status === 'golden' ? 'Golden Data' : 'Vyžaduje validaci'}
                </span>
              </div>
            </div>

            <div className="p-4 h-full overflow-y-auto">
              {editedData && Object.entries(editedData).map(([key, value]) => {
                if (key === 'meta') return null; // Skip meta data
                // Check if field was originally empty
                const originalValue = document?.initialExtractionData?.[key];
                const wasOriginallyEmpty = originalValue === null || originalValue === undefined || originalValue === '';
                const isCurrentlyEmpty = value === null || value === undefined || value === '';
                
                // Only skip if it was originally empty AND is currently empty
                if (wasOriginallyEmpty && isCurrentlyEmpty) return null;
                
                return renderField(key, value);
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <Keyboard className="h-5 w-5 mr-2" />
                  Nápověda pro validaci dokumentů
                </h2>
                <button
                  onClick={() => setShowHelp(false)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">🎯 Účel validace</h3>
                  <p className="text-gray-600 mb-2">
                    Validace slouží k vytvoření "golden standard" dat - tedy správných hodnot, 
                    které budou použity pro měření přesnosti automatického vytěžování.
                  </p>
                  <p className="text-gray-600">
                    Zkontrolujte každé pole a označte ho jako ověřené, pokud je hodnota správná.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">⌨️ Klávesové zkratky</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex justify-between">
                        <span className="font-mono bg-gray-200 px-2 py-1 rounded text-sm">Enter</span>
                        <span className="text-sm text-gray-600">Ověřit aktuální pole</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-mono bg-gray-200 px-2 py-1 rounded text-sm">↓ / j</span>
                        <span className="text-sm text-gray-600">Další pole</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-mono bg-gray-200 px-2 py-1 rounded text-sm">↑ / k</span>
                        <span className="text-sm text-gray-600">Předchozí pole</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-mono bg-gray-200 px-2 py-1 rounded text-sm">Ctrl+S</span>
                        <span className="text-sm text-gray-600">Uložit změny</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-mono bg-gray-200 px-2 py-1 rounded text-sm">?</span>
                        <span className="text-sm text-gray-600">Zobrazit nápovědu</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-mono bg-gray-200 px-2 py-1 rounded text-sm">Esc</span>
                        <span className="text-sm text-gray-600">Zavřít nápovědu</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">🔄 Workflow</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-600">
                    <li>Zkontrolujte vytěžená data proti originálnímu dokumentu vlevo</li>
                    <li>Opravte nesprávné hodnoty v polích formuláře</li>
                    <li>Stiskněte <kbd className="bg-gray-200 px-1 rounded">Enter</kbd> pro označení pole jako ověřené</li>
                    <li>Pokračujte na další pole pomocí <kbd className="bg-gray-200 px-1 rounded">↓</kbd> nebo <kbd className="bg-gray-200 px-1 rounded">j</kbd></li>
                    <li>Po dokončení validace uložte změny pomocí <kbd className="bg-gray-200 px-1 rounded">Ctrl+S</kbd></li>
                  </ol>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">💡 Tipy</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li>Modrý rám označuje aktuálně vybrané pole</li>
                    <li>Zelené pole znamená ověřené a uzamčené proti editaci</li>
                    <li>Pro datumová pole používejte formát YYYY-MM-DD</li>
                    <li>Prázdná pole označte jako ověřená, pokud skutečně nemají hodnotu</li>
                    <li>Progress bar ukazuje kolik procent polí je již ověřeno</li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowHelp(false)}
                  className="btn-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Rozumím, zavřít nápovědu
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};